import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';
import { useSwitchChain } from 'wagmi';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { RecommendedWalletApp } from '#src/components/wallet/RecommendedWalletApp/index.tsx';
import { useBlockchainContext } from '#src/contexts/blockchain/index.ts';
import { glossary } from '#src/lib/glossary.ts';

const messages = defineMessages({
  wrongNetwork: {
    defaultMessage:
      'Please switch your Wallet to the Ethereum {expectedEthereumNetwork} network.',
  },
});

const Warning = styled(BodyM)`
  padding: 10px;
  color: var(--c-nd-600);
  border-radius: 8px;
  background: var(--c-nd-50);
`;

const Icon = styled.img`
  max-width: var(--triple-unit);
  max-height: var(--triple-unit);
`;

export const WalletButton = ({
  icon,
  name,
  onClick,
  loading = false,
}: {
  icon?: string;
  name: string;
  loading?: boolean;
  onClick?: () => void;
}) => {
  return (
    <Button
      startIcon={icon && !loading ? <Icon src={icon} alt={name} /> : null}
      fullWidth
      size="medium"
      color="primary"
      loading={loading}
      onClick={onClick}
    >
      <FormattedMessage
        defaultMessage="Connect with {name}"
        values={{ name }}
      />
    </Button>
  );
};

export const ConnectPrivateWallet = () => {
  const { chains, switchChain } = useSwitchChain();
  const {
    expectedEthereumNetwork,
    connectors,
    isConnected,
    loading,
    activeConnector,
    wrongChain,
    connectToEthereum,
    reset,
  } = useBlockchainContext();

  if (loading) {
    return (
      <>
        <LoadingIndicator />
        <Button onClick={reset} size="medium" color="secondary">
          <FormattedMessage {...glossary.cancel} />
        </Button>
      </>
    );
  }

  if (connectors.length === 0 && !activeConnector) {
    return <RecommendedWalletApp />;
  }

  if (wrongChain) {
    return (
      <Vertical>
        <Warning>
          <FormattedMessage
            {...messages.wrongNetwork}
            values={{
              expectedEthereumNetwork: expectedEthereumNetwork?.name || '',
            }}
          />
        </Warning>
        {!!expectedEthereumNetwork &&
          chains
            .filter(chain => chain.id === expectedEthereumNetwork.id)
            .map(chain => (
              <Button
                size="medium"
                key={chain.id}
                onClick={() => switchChain({ chainId: chain.id })}
              >
                <FormattedMessage
                  defaultMessage="Switch to {name}"
                  values={{ name: expectedEthereumNetwork.name }}
                />
              </Button>
            ))}
      </Vertical>
    );
  }
  if (isConnected) return null;

  return connectors.map(connector => (
    <WalletButton
      key={connector.id}
      icon={connector.icon}
      name={connector.name}
      onClick={() => connectToEthereum(connector)}
    />
  ));
};
